import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { X, Loader2 } from "lucide-react";
import { addLoanType, updateLoanType, fetchAllLoanTypes } from "../../store/slices/loanDetailsSlice";
import useToast from "../../utils/useToast";
import Toast from "../Toast/Toast";

const INPUT_CLASS = `
  w-full h-10 rounded-lg border border-surface-200 bg-surface-50/60 px-3 text-sm text-surface-800
  outline-none transition-all focus:bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-500/10
`;

const INITIAL_FORM = {
  categoryCode: "M",
  typeName: "",
  description: "",
  isActive: true,
};

export default function LoanTypesDialog({ open, onClose, onSuccess, onError, mode = "add", loanType = null }) {
  const isView = mode === "view";
  const isEdit = mode === "edit";
  const dispatch = useDispatch();
  // const { toast, showToast, hideToast } = useToast();

  const [form, setForm] = useState(INITIAL_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (!open) {
      setForm(INITIAL_FORM);
      setErrors({});
      return;
    }

    if (loanType) {
      setForm({
        categoryCode: loanType.categoryCode || "M",
        typeName: loanType.typeName || "",
        description: loanType.description || "",
        isActive: loanType.isActive ?? true,
      });
    } else {
      setForm(INITIAL_FORM);
    }
  }, [open, loanType]);

  const handleSave = async () => {
    if (isView) {
      onClose();
      return;
    }

    const vErrors = {};
    if (!form.typeName.trim()) {
      vErrors.typeName = "Loan type name is required.";
    }
    setErrors(vErrors);
    if (Object.keys(vErrors).length > 0) return;

    setSaving(true);
    try {
      if (isEdit) {
        await dispatch(
          updateLoanType({
            loanTypeId: loanType.id,
            categoryCode: form.categoryCode,
            typeName: form.typeName.trim(),
            description: form.description,
            isActive: form.isActive,
          })
        ).unwrap();
        onSuccess?.("Loan type updated successfully.");
      } else {
        await dispatch(
          addLoanType({
            categoryCode: form.categoryCode,
            typeName: form.typeName.trim(),
            description: form.description,
          })
        ).unwrap();
        onSuccess?.("Loan type created successfully.");
      }
      dispatch(fetchAllLoanTypes());
      onClose();
    } catch (err) {
      onError?.(err || "Failed to save loan type.");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-surface-900/40 backdrop-blur-xs transition-opacity" onClick={onClose} />
      <div className="relative z-10 w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-100">
          <h2 className="text-base font-semibold text-surface-900">
            {isView ? "Loan Type Details" : isEdit ? "Edit Loan Type" : "New Loan Type"}
          </h2>
          <button type="button" onClick={onClose} className="rounded-full p-1.5 text-surface-400 hover:bg-surface-100">
            <X size={16} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-surface-600 mb-1">Loan Category</label>
            <select
              value={form.categoryCode}
              disabled={isView}
              onChange={(e) => setForm((p) => ({ ...p, categoryCode: e.target.value }))}
              className={`${INPUT_CLASS} appearance-auto`}
            >
              <option value="M">Loan</option>
              <option value="O">Other Loan</option>
            </select>
          </div>

          <div>
            <label className="block text-xs font-medium text-surface-600 mb-1">Type Name</label>
            <input
              type="text"
              value={form.typeName}
              disabled={isView}
              placeholder="e.g. Distress Loan, Housing Loan"
              onChange={(e) => {
                setForm((p) => ({ ...p, typeName: e.target.value }));
                setErrors((p) => ({ ...p, typeName: "" }));
              }}
              className={`${INPUT_CLASS} ${errors.typeName ? "border-red-400" : ""}`}
            />
            {errors.typeName && <p className="mt-1 text-xs text-red-500">{errors.typeName}</p>}
          </div>

          <div>
            <label className="block text-xs font-medium text-surface-600 mb-1">Description</label>
            <textarea
              rows={2}
              value={form.description}
              disabled={isView}
              placeholder="Optional notes or details..."
              onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              className={`${INPUT_CLASS} h-auto py-2 resize-none`}
            />
          </div>

          {isEdit && (
            <label className="flex items-center gap-2 cursor-pointer pt-1">
              <input
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))}
                className="rounded border-surface-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="text-xs font-medium text-surface-700">Active</span>
            </label>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-6 py-4 bg-surface-50 border-t border-surface-100">
          <button type="button" onClick={onClose} className="rounded-lg px-4 py-2 text-xs font-medium text-surface-600 hover:bg-surface-100">
            {isView ? "Close" : "Cancel"}
          </button>
          {!isView && (
            <button
              type="button"
              disabled={saving}
              onClick={handleSave}
              className="inline-flex items-center gap-1.5 rounded-lg bg-primary-600 px-4 py-2 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
            >
              {saving && <Loader2 size={13} className="animate-spin" />}
              {saving ? "Saving..." : isEdit ? "Update" : "Save"}
            </button>
          )}
        </div>
      </div>
      {/* <Toast message={toast?.message} type={toast?.type} onClose={hideToast} /> */}
    </div>
  );
}